import { useState, useEffect, lazy, Suspense } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import HeroSection from "@/components/HeroSection";
import InvestmentForm, { type InvestmentData } from "@/components/InvestmentForm";
import ResultsDashboard from "@/components/ResultsDashboard";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { useCanonical } from "@/hooks/useCanonical";
import { LogIn, LogOut, User, BarChart3, ArrowRight, Bell, Globe } from "lucide-react";
import { CURRENCIES, getCurrencySymbol, getConversionRate } from "@/lib/currencies";
import { pushEvent } from "@/lib/datalayer";
import MobileStickyBar from "@/components/MobileStickyBar";
import LanguageSwitcher from "@/components/LanguageSwitcher";

// Below-the-fold: lazy-load to shrink initial bundle and speed up LCP
const PricingSection = lazy(() => import("@/components/PricingSection"));
const MarketPulseTeaser = lazy(() => import("@/components/MarketPulseTeaser"));
const SocialProofStrip = lazy(() => import("@/components/SocialProofStrip"));
const HowItWorks = lazy(() => import("@/components/HowItWorks"));
const FreeTierBanner = lazy(() => import("@/components/FreeTierBanner"));
// ExitIntentPopup removed: was overlapping with MobileStickyBar (2s) causing dual-nag bounces.
const LandingFAQ = lazy(() => import("@/components/LandingFAQ"));
const TrendingStocksWidget = lazy(() => import("@/components/TrendingStocksWidget"));
const Testimonials = lazy(() => import("@/components/Testimonials"));
const ObjectionsSection = lazy(() => import("@/components/ObjectionsSection"));
const SecondCTA = lazy(() => import("@/components/SecondCTA"));


type View = "landing" | "form" | "results";

const Index = () => {
  const { t } = useLanguage();
  const { user, signOut, loading: authLoading } = useAuth();
  useCanonical("/", "Get AI-powered stock recommendations tailored to your investment goals, risk appetite, and timeline. Projected ROI included.", "Signal GPT - AI-Powered Stock Recommendations");
  // Avoid a flash of the landing page when returning from /auth with a pending submission
  const [view, setView] = useState<View>(() => {
    try {
      if (typeof window !== "undefined" && sessionStorage.getItem("pendingInvestment")) {
        return "form";
      }
    } catch {}
    return "landing";
  });
  const [selectedPlan, setSelectedPlan] = useState("");
  const [selectedPrice, setSelectedPrice] = useState(0);
  const [investmentData, setInvestmentData] = useState<InvestmentData | null>(null);
  const [runsRemaining, setRunsRemaining] = useState<number | null>(null);
  const [accessOrigin, setAccessOrigin] = useState<"subscription" | "payment" | "granted" | null>(null);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [activeAccessKey, setActiveAccessKey] = useState<string | null>(null);
  const [userCurrency, setUserCurrency] = useState("USD");
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [subscriptionPeriodEnd, setSubscriptionPeriodEnd] = useState<string | null>(null);
  const [totalRuns, setTotalRuns] = useState<number | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch total individual stock signals across all reports (social proof)
  useEffect(() => {
    supabase.rpc("get_total_signals_count").then(({ data }) => {
      if (typeof data === "number" && data > 0) setTotalRuns(data);
    });
  }, []);

  // UTM detection: auto-scroll to pricing for Instagram traffic
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get("utm_source");
    if (utmSource) {
      pushEvent({ event: "page_view" as any, page_type: "home" });
      if (utmSource === "instagram") {
        setTimeout(() => {
          const el = document.getElementById("pricing");
          el?.scrollIntoView({ behavior: "smooth" });
        }, 800);
      }
    }
  }, []);

  // Fetch user currency from profile
  useEffect(() => {
    if (!user) return;
    supabase.from("profiles").select("currency").eq("id", user.id).single().then(({ data }) => {
      if (data?.currency) setUserCurrency(data.currency);
    });
  }, [user]);

  // Geo-detect currency for anonymous visitors (90% mobile / FB traffic from IN, SA, etc.)
  // Non-blocking, cached, falls back silently to USD on any failure.
  useEffect(() => {
    if (user) return;
    try {
      const cached = localStorage.getItem("geoCurrency");
      if (cached && CURRENCIES.some((c) => c.code === cached)) {
        setUserCurrency(cached);
        return;
      }
    } catch {}
    const COUNTRY_TO_CURRENCY: Record<string, string> = {
      IN: "INR", SA: "SAR", AE: "AED", PK: "PKR", BD: "BDT", OM: "OMR",
      EG: "EGP", KW: "KWD", QA: "QAR", BH: "BHD", JO: "JOD", LB: "LBP",
      MA: "MAD", TR: "TRY", ID: "IDR", MY: "MYR", PH: "PHP", TH: "THB",
      VN: "VND", NG: "NGN", ZA: "ZAR", KE: "KES", BR: "BRL", MX: "MXN",
      AR: "ARS", CL: "CLP", CO: "COP", GB: "GBP", DE: "EUR", FR: "EUR",
      ES: "EUR", IT: "EUR", NL: "EUR", IE: "EUR", PT: "EUR", AT: "EUR",
      BE: "EUR", FI: "EUR", GR: "EUR", CA: "CAD", AU: "AUD", NZ: "NZD",
      JP: "JPY", KR: "KRW", CN: "CNY", HK: "HKD", SG: "SGD", CH: "CHF",
      SE: "SEK", NO: "NOK", DK: "DKK", PL: "PLN", CZ: "CZK", RU: "RUB",
      UA: "UAH",
    };
    const controller = new AbortController();
    fetch("https://ipapi.co/json/", { signal: controller.signal })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        const cc = d?.country_code as string | undefined;
        const curr = cc ? COUNTRY_TO_CURRENCY[cc] : undefined;
        if (curr && CURRENCIES.some((c) => c.code === curr)) {
          setUserCurrency(curr);
          try { localStorage.setItem("geoCurrency", curr); } catch {}
        }
      })
      .catch(() => {});
    return () => controller.abort();
  }, [user]);


  const fetchUserAccess = async () => {
    if (!user) {
      setRunsRemaining(null);
      setSelectedPlan("");
      setSelectedPrice(0);
      setAccessOrigin(null);
      setSubscriptionStatus(null);
      setSubscriptionPeriodEnd(null);
      return;
    }

    try {
      // Auto-link any granted access or payment sessions matching this user's email
      if (user.email) {
        await Promise.all([
          supabase.rpc("link_granted_access_by_email", {
            p_user_id: user.id,
            p_email: user.email,
          }),
          supabase.rpc("link_payment_sessions_by_email", {
            p_user_id: user.id,
            p_email: user.email,
          }),
        ]);
      }

      // Try check-subscription first (refreshes from CREEM)
      try {
        const { data: subData } = await supabase.functions.invoke("check-subscription");
        if (subData && !subData.error) {
          const result = subData as any;
          
          // Priority: subscription > payment > granted
          if (result.subscription && result.subscription_remaining > 0) {
            setAccessOrigin("subscription");
            setRunsRemaining(result.subscription_remaining);
            setSelectedPlan(result.subscription.plan);
            setSelectedPrice(0);
            setActiveSessionId(null);
            setActiveAccessKey(null);
            setSubscriptionStatus(result.subscription.status);
            setSubscriptionPeriodEnd(result.subscription.current_period_end);
            return;
          }
          
          if (result.payment && result.payment_remaining > 0) {
            setAccessOrigin("payment");
            setRunsRemaining(result.payment_remaining);
            setSelectedPlan(result.payment.plan);
            setSelectedPrice(Number(result.payment.price ?? 0));
            setActiveSessionId(result.payment.session_id);
            setActiveAccessKey(null);
            setSubscriptionStatus(null);
            setSubscriptionPeriodEnd(null);
            return;
          }
          
          if (result.granted && result.granted_remaining > 0) {
            setAccessOrigin("granted");
            setRunsRemaining(result.granted_remaining);
            setSelectedPlan(result.granted.plan || "Pro");
            setSelectedPrice(0);
            setActiveAccessKey(result.granted.access_key);
            setActiveSessionId(null);
            setSubscriptionStatus(null);
            setSubscriptionPeriodEnd(null);
            return;
          }

          // Has subscription info but no remaining runs
          if (result.subscription) {
            setSubscriptionStatus(result.subscription.status);
            setSubscriptionPeriodEnd(result.subscription.current_period_end);
            setSelectedPlan(result.subscription.plan);
          }

          setRunsRemaining(null);
          setAccessOrigin(null);
          return;
        }
      } catch (subErr) {
        console.warn("check-subscription failed, falling back to RPC:", subErr);
      }

      // Fallback to direct RPC
      const { data, error } = await supabase.rpc("lookup_user_access", { p_user_id: user.id });
      if (error) {
        console.error("lookup_user_access error:", error);
        return;
      }

      const result = data as any;
      
      if (result.subscription && result.subscription_remaining > 0) {
        setAccessOrigin("subscription");
        setRunsRemaining(result.subscription_remaining);
        setSelectedPlan(result.subscription.plan);
        setSelectedPrice(0);
        setActiveSessionId(null);
        setActiveAccessKey(null);
        setSubscriptionStatus(result.subscription.status);
        setSubscriptionPeriodEnd(result.subscription.current_period_end);
      } else if (result.payment_remaining > 0 && result.payment) {
        setAccessOrigin("payment");
        setRunsRemaining(result.payment_remaining);
        setSelectedPlan(result.payment.plan);
        setSelectedPrice(Number(result.payment.price ?? 0));
        setActiveSessionId(result.payment.session_id);
        setActiveAccessKey(null);
      } else if (result.granted_remaining > 0 && result.granted) {
        setAccessOrigin("granted");
        setRunsRemaining(result.granted_remaining);
        setSelectedPlan(result.granted.plan || "Pro");
        setSelectedPrice(0);
        setActiveAccessKey(result.granted.access_key);
        setActiveSessionId(null);
      } else {
        setRunsRemaining(null);
        setSelectedPlan("");
        setSelectedPrice(0);
        setAccessOrigin(null);
        setActiveSessionId(null);
        setActiveAccessKey(null);
      }
    } catch (err) {
      console.error("Failed to fetch user access:", err);
    }
  };

  useEffect(() => {
    const state = location.state as { paid?: boolean; plan?: string; price?: number } | null;
    if (state?.paid && state.plan) {
      setSelectedPlan(state.plan);
      setSelectedPrice(state.price || 0);
      setView("form");
      window.history.replaceState({}, document.title);
      setTimeout(fetchUserAccess, 500);
    }
  }, [location.state]);

  useEffect(() => {
    if (!authLoading && view === "landing") {
      fetchUserAccess();
    }
  }, [user, authLoading]);

  // Resume pending submission after soft-defer auth (anonymous user signed in)
  useEffect(() => {
    if (!user || authLoading) return;
    let pending: InvestmentData | null = null;
    try {
      const raw = sessionStorage.getItem("pendingInvestment");
      if (raw) pending = JSON.parse(raw);
    } catch {}
    if (!pending) return;

    (async () => {
      // Ensure user has runs: refresh access first
      await fetchUserAccess();
      // Re-read access fresh from the RPC (state may not be flushed yet)
      const { data: access } = await supabase.rpc("lookup_user_access", { p_user_id: user.id });
      const a = access as any;
      const totalRemaining =
        (a?.subscription_remaining || 0) +
        (a?.payment_remaining || 0) +
        (a?.granted_remaining || 0);

      if (totalRemaining <= 0) {
        // Try to auto-activate Lite (free 1 run)
        try {
          const { data, error } = await supabase.functions.invoke("register-payment", {
            body: { plan: "Lite", price: 0, billing_period: "monthly" },
          });
          if (error) throw error;
          if (data?.session_id) {
            setSelectedPlan("Lite");
            setSelectedPrice(0);
            setAccessOrigin("payment");
            setActiveSessionId(data.session_id);
            setRunsRemaining(1);
          } else {
            throw new Error("No session returned");
          }
        } catch (e) {
          console.error("Auto-Lite failed:", e);
          // Keep their form data: drop them on the prefilled form, don't burn a run
          toast.message("Welcome back! Your details are saved.", {
            description: "Choose a plan to run your AI signal.",
          });
          setView("form");
          window.scrollTo({ top: 0, behavior: "smooth" });
          return;
        }
      }

      // Increment the run counter
      const { data: incResult } = await supabase.rpc("increment_user_run", { p_user_id: user.id });
      const r = incResult as any;
      if (!r?.success) {
        // Couldn't burn a run - keep details, send to form
        toast.message("Welcome back! Your details are saved.", {
          description: "Choose a plan to run your AI signal.",
        });
        setView("form");
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      setRunsRemaining(Math.max((r.runs_allowed ?? 1) - (r.runs_used ?? 1), 0));

      // Success: consume the pending payload and show results
      sessionStorage.removeItem("pendingInvestment");
      // Normalize plan on the pending payload: it was captured anonymously and may be empty.
      // Resolve to the currently active tier (defaults to Lite for the auto-activated free run).
      const resolvedPlan =
        (a?.subscription?.plan as string | undefined) ||
        (a?.payment?.plan as string | undefined) ||
        (a?.granted?.plan as string | undefined) ||
        "Lite";
      setInvestmentData({ ...pending, plan: resolvedPlan, price: pending.price ?? 0 });
      setView("results");
      toast.success("Welcome back! Generating your AI signal...");
      window.scrollTo({ top: 0, behavior: "smooth" });
    })();
  }, [user, authLoading]);


  const handleSelectPlan = async (plan: string, price: number, billingPeriod?: string) => {
    pushEvent({ event: "select_plan", plan_type: plan.toLowerCase(), plan_duration: billingPeriod || "monthly", plan_price: price, login_status: user ? "logged_in" : "anonymous", page_type: "home" });
    if (!user) {
      navigate("/auth");
      return;
    }
    if (plan === "Lite" && price === 0) {
      try {
        const { data, error } = await supabase.functions.invoke("register-payment", {
          body: { plan: "Lite", price: 0, billing_period: "monthly" },
        });
        if (error) throw error;
        if (data?.session_id) {
          toast.success("Lite plan activated! You have 1 free run.");
          setSelectedPlan("Lite");
          setSelectedPrice(0);
          setAccessOrigin("payment");
          setActiveSessionId(data.session_id);
          setRunsRemaining(1);
          setView("form");
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      } catch (err) {
        console.error("Lite registration error:", err);
        toast.error("Failed to activate Lite plan. Please try again.");
      }
      return;
    }
    navigate(`/checkout?plan=${encodeURIComponent(plan)}&price=${price}&billing=${billingPeriod || "monthly"}`);
  };

  const handleFormSubmit = async (data: InvestmentData) => {
    // Soft-defer auth: anonymous users get sent to /auth and we resume after login
    if (!user) {
      try {
        sessionStorage.setItem("pendingInvestment", JSON.stringify(data));
      } catch {}
      pushEvent({ event: "submit_investment_form", plan_type: "lite", investment_amount: data.amount, investment_goal: data.goal, investment_horizon: data.investmentHorizon, login_status: "anonymous", page_type: "home" });
      toast.message("Sign in to get your free AI signal", { description: "Takes 10 seconds. Your form is saved." });
      navigate("/auth?next=run");
      return;
    }
    pushEvent({ event: "submit_investment_form", plan_type: selectedPlan.toLowerCase(), investment_amount: data.amount, investment_goal: data.goal, investment_horizon: data.investmentHorizon, login_status: "logged_in", page_type: "home", runs_remaining: runsRemaining ?? undefined });

    // Run limit enforcement and increment now happens server-side in stock-advisor.
    // Client no longer decrements counters to avoid double-charging.


    // Guard: never submit with an empty plan (stock-advisor rejects it).
    const safePlan = data.plan && ["Lite", "Plus", "Pro"].includes(data.plan)
      ? data.plan
      : (selectedPlan && ["Lite", "Plus", "Pro"].includes(selectedPlan) ? selectedPlan : "Lite");
    setInvestmentData({ ...data, plan: safePlan });
    setView("results");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => {
    if (view === "results" && runsRemaining !== null && runsRemaining > 0) {
      setView("form");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setView("landing");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleRunAgain = () => {
    pushEvent({ event: "click_run_again", plan_type: selectedPlan.toLowerCase(), login_status: "logged_in", page_type: "home", runs_remaining: runsRemaining ?? undefined });
    if (runsRemaining !== null && runsRemaining > 0) {
      setView("form");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      toast.error("No runs remaining. Please purchase a new plan.");
      setView("landing");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // While we resume a pending submission after signup, show a loader instead of
  // flashing the landing page or an empty form.
  const isResuming = (() => {
    try { return typeof window !== "undefined" && !!sessionStorage.getItem("pendingInvestment"); }
    catch { return false; }
  })();
  if (isResuming && view !== "results") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
          <p className="text-sm font-display">Preparing your AI signal…</p>
        </div>
      </div>
    );
  }

  if (view === "results" && investmentData) {
    return <ResultsDashboard investmentData={investmentData} onBack={handleBack} runsRemaining={runsRemaining} onRunAgain={handleRunAgain} currency={userCurrency} />;
  }

  if (view === "form") {
    return (
      <InvestmentForm
        plan={selectedPlan} price={selectedPrice} onSubmit={handleFormSubmit}
        onBack={() => { setView("landing"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
        runsRemaining={runsRemaining}
        currency={userCurrency}
      />
    );
  }

  const hasRuns = runsRemaining !== null && runsRemaining > 0;

  return (
    <div className={!hasRuns ? "pb-24 md:pb-0" : undefined}>

      <HeroSection totalRuns={totalRuns} onGetStarted={async () => {
        pushEvent({ event: "click_get_started", login_status: user ? "logged_in" : "anonymous", page_type: "home" });
        // Logged-in users with no active tier: auto-activate Lite so the form has a valid plan.
        if (user && !selectedPlan) {
          try {
            const { data } = await supabase.functions.invoke("register-payment", {
              body: { plan: "Lite", price: 0, billing_period: "monthly" },
            });
            if (data?.session_id) {
              setSelectedPlan("Lite");
              setSelectedPrice(0);
              setAccessOrigin("payment");
              setActiveSessionId(data.session_id);
              setRunsRemaining(1);
            }
          } catch (e) { console.error("Auto-Lite on get-started failed:", e); }
        }
        // Anonymous users will be asked to sign in at submit time
        setView("form");
        requestAnimationFrame(() => window.scrollTo(0, 0));
      }} plan={selectedPlan} />

      {hasRuns && (
        <div className="max-w-6xl mx-auto px-4 -mt-4 mb-8">
          <div className="rounded-2xl border border-border bg-gold/5 p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <p className="text-lg font-display font-bold">
                <span className="text-gold">{runsRemaining} {runsRemaining !== 1 ? t("index.runs") : t("index.run")}</span>{" "}
                {t("index.runsRemaining")}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                <span className="font-semibold text-foreground">{selectedPlan}</span> {t("index.plan")}
                {subscriptionStatus === "active" && subscriptionPeriodEnd && (
                  <span className="ml-2 text-xs text-gold">
                    Active until {new Date(subscriptionPeriodEnd).toLocaleDateString()}
                  </span>
                )}
                {subscriptionStatus === "canceled" && (
                  <span className="ml-2 text-xs text-yellow-400">
                    Canceled (active until period end)
                  </span>
                )}
              </p>
            </div>
            <button
              onClick={() => { setView("form"); requestAnimationFrame(() => window.scrollTo(0, 0)); }}
              className="px-8 py-3 rounded-xl bg-gold text-primary-foreground text-sm font-display font-bold hover:bg-gold/90 transition-colors shadow-gold flex items-center gap-2"
            >
              {t("index.continue")} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <Suspense fallback={null}>
        <FreeTierBanner onGetStarted={() => {
          if (user) {
            handleSelectPlan("Lite", 0);
          } else {
            navigate("/auth");
          }
        }} />



        <TrendingStocksWidget />

        <HowItWorks onGetStarted={() => {
          pushEvent({ event: "click_get_started", login_status: user ? "logged_in" : "anonymous", page_type: "home" });
          setView("form");
          requestAnimationFrame(() => window.scrollTo(0, 0));
        }} />

        <MarketPulseTeaser />
        <SocialProofStrip />
        <Testimonials />
        <ObjectionsSection />

        {/* Show pricing section - users can always buy more */}
        <div id="pricing">
          <PricingSection onSelectPlan={handleSelectPlan} currency={userCurrency} />
        </div>

        <LandingFAQ />

        <SecondCTA onGetStarted={() => {
          pushEvent({ event: "click_get_started", login_status: user ? "logged_in" : "anonymous", page_type: "home" });
          setView("form");
          requestAnimationFrame(() => window.scrollTo(0, 0));
        }} />



        {/* Mobile sticky CTA for visitors without runs */}
        {!hasRuns && (
          <MobileStickyBar onGetStarted={() => {
            const el = document.getElementById("pricing");
            el?.scrollIntoView({ behavior: "smooth" });
          }} />
        )}

        {/* Exit intent popup removed - conflicted with MobileStickyBar */}

      </Suspense>


      <footer className="py-8 text-center text-xs text-muted-foreground border-t border-border space-y-3 px-4">
        <p className="font-display">{t("footer.disclaimer")}</p>
        <p className="font-display text-muted-foreground max-w-2xl mx-auto">{t("footer.aiDisclaimer")}</p>
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 pt-2">
          <Link to="/news" className="font-display text-gold/60 hover:text-gold transition-colors underline underline-offset-2">{t("footer.news")}</Link>
          <span className="text-border hidden sm:inline">•</span>
          <Link to="/p/best-stock-market-apps-by-region" className="font-display text-gold/60 hover:text-gold transition-colors underline underline-offset-2">Blog</Link>
          <span className="text-border hidden sm:inline">•</span>
          <Link to="/faq" className="font-display text-gold/60 hover:text-gold transition-colors underline underline-offset-2">FAQ</Link>
          <span className="text-border hidden sm:inline">•</span>
          <Link to="/privacy" className="font-display text-gold/60 hover:text-gold transition-colors underline underline-offset-2">{t("footer.privacy")}</Link>
          <span className="text-border hidden sm:inline">•</span>
          <Link to="/terms" className="font-display text-gold/60 hover:text-gold transition-colors underline underline-offset-2">{t("footer.terms")}</Link>
          <span className="text-border hidden sm:inline">•</span>
          <a href="mailto:signalgpt@protonmail.com" className="font-display text-gold/60 hover:text-gold transition-colors underline underline-offset-2">{t("footer.contact")}</a>
        </div>
      </footer>
    </div>
  );
};

export default Index;
